import { DDSPagination, DDSTable, DDSTypography } from 'den-design-system'
import React, { CSSProperties, useEffect, useState } from 'react'

import Spinner from '../../Spinner/Spinner'
import StringConstants from '../../../../constants/StringConstants'
import pallete from '../../../../global/newPallete'

interface TableProps {
  id?: string
  data: any
  columns: {
    heading: React.ReactNode | string
    dataLabel: string
    render: (item: any, fullItem?: any, index?: number) => React.ReactNode
    columnHeaderStyle?: React.CSSProperties
    columnDataStyle?: React.CSSProperties
  }[]
  maxHeight?: string
  selectable?: boolean
  onSelect?: (selectedRow: any) => void
  tableStyle?: CSSProperties
  className?: string
  loading?: boolean
  paginationStyle?: CSSProperties
  onPageChange?: (pageNumber: number) => void
  totalCount: number
  currentPage?: number
  rowsPerPage?: number
  paginationCount?: number
  disabled?: boolean
  showPagination?: boolean
  selectionType?: 'checkbox' | 'radio'
  overrideHeight?: boolean
  clickable?: (id?: any, type?: any) => void
  clickType?: string
  disableRowsOnSelect?: boolean
  padding?: boolean
}

const Table: React.FC<TableProps> = ({
  id,
  data,
  columns,
  selectable = false,
  onSelect = () => null,
  tableStyle,
  className,
  loading = false,
  paginationStyle,
  onPageChange = () => null,
  totalCount,
  maxHeight,
  currentPage = 1,
  rowsPerPage = StringConstants.DEFAULT_TABLE_SIZE,
  paginationCount,
  disabled = false,
  showPagination = true,
  selectionType = 'radio',
  overrideHeight = false,
  clickable,
  clickType,
  padding = true
}) => {
  const [disableRowsSelect, setDisableRowSelect] = useState(false)
  const [selectedRows, setSelectedRows] = useState<any[]>([])

  useEffect(() => {
    if (selectedRows.length === 0) {
      setDisableRowSelect(false)
    }
  }, [selectedRows])
  const handleRowSelect = (selectedRow: any[]) => {
    setSelectedRows(selectedRow)
    if (selectedRow.length > 0) {
      setDisableRowSelect(selectable && selectionType === 'checkbox')
    }
    onSelect(selectedRow)
  }

  return (
    <>
      {data?.length > 0 ? (
        <div
          className={`flex flex-col z-[9] relative  w-full override-overflow table-non-interactive border ${padding ? 'rounded-lg' : ''} `}
        >
          <div
            className={`loader-time  ${overrideHeight ? '2xl:h-[50vh] h-[47vh] ' : ''}`}
          >
            <DDSTable
              id={id}
              columns={columns}
              data={data}
              style={{ ...tableStyle, overflow: 'auto' }}
              selectable={selectable}
              onSelect={(selectedRow) => {
                handleRowSelect(selectedRow)
              }}
              maxHeight={maxHeight}
              className={`${className} table-non-interactive`}
              loading={loading}
              shape='roundedRectangle'
              selectionType={selectionType}
              disableRowsOnSelect={disableRowsSelect}
              {...(clickable && {
                onRowClick: (rowData: any) => {
                  const activeElement = document.activeElement
                  if (activeElement?.getAttribute('type') === 'button') {
                    return
                  }
                  if (
                    activeElement?.tagName === 'INPUT' &&
                    activeElement?.getAttribute('type') === 'checkbox'
                  ) {
                    return
                  }
                  if (clickType === 'GroupException') {
                    const exceptionGroupId = rowData.exceptionGroupId
                    const exceptionGroupType = rowData.exceptionGroupType
                    clickable(exceptionGroupType || 'default', exceptionGroupId)
                  } else if (clickType === 'AllException') {
                    const exceptionId = rowData.id
                    const exceptionType = rowData.exceptionType
                    clickable(exceptionId, exceptionType || 'default')
                  } else if (clickType === 'GroupExceptionLog') {
                    const groupLogExceptionLogId = rowData.id
                    const groupLogExceptionType = rowData.exceptionType
                    clickable(
                      groupLogExceptionLogId,
                      groupLogExceptionType || 'default'
                    )
                  } else if (
                    clickType === 'AllApis' ||
                    clickType === 'GroupURLViewApiLogs' ||
                    clickType === 'GroupUserViewApiLogs'
                  ) {
                    const apiId = rowData.id
                    clickable(apiId)
                  } else if (
                    clickType === 'GroupApiURLView' ||
                    clickType === 'GroupApiUserView'
                  ) {
                    const groupApiId = rowData.id
                    clickable(groupApiId)
                  } else if (clickType === 'Incident') {
                    const incidentId = rowData.id
                    clickable(incidentId)
                  } else if (clickType === 'Escalation') {
                    const escalationSetId = rowData.id
                    clickable(escalationSetId)
                  } else if (clickType === 'Uptime') {
                    clickable(rowData.id)
                  } else if (clickType === 'Job') {
                    clickable(rowData.id)
                  } else if (clickType === 'ucLog') {
                    clickable(rowData)
                  } else if (clickType === 'SalesforceAudit') {
                    const id = rowData.Id
                    clickable(id)
                  } else if (
                    clickType === 'SalesforcePermissionSet' ||
                    clickType === 'SalesforceProfiles' ||
                    clickType === 'SalesforceRoles' ||
                    clickType === 'SalesforceLicenses'
                  ) {
                    const id = rowData.Id
                    clickable(id)
                  } else if (clickType === 'ProjectSettings') {
                    const id = rowData.id
                    clickable(id)
                  }
                }
              })}
            />
          </div>
          {!loading && showPagination && (
            <div className='flex justify-between px-[16px] py-[12px] w-full table-width'>
              <div
                className='flex justify-start items-center text-center'
                style={{ borderColor: pallete.colors.stroke2 }}
              >
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark6}
                >
                  {Math.min(
                    (currentPage - 1) * rowsPerPage + 1,
                    totalCount as number
                  )}{' '}
                  - {Math.min(currentPage * rowsPerPage, totalCount as number)}{' '}
                  of {totalCount}
                </DDSTypography.Paragraph>
              </div>
              <div className='flex justify-end'>
                <DDSPagination
                  buttonVariant='icon'
                  shape='roundedRectangle'
                  style={paginationStyle}
                  currentPage={currentPage}
                  totalCount={totalCount}
                  paginationCount={paginationCount}
                  rowsPerPage={rowsPerPage}
                  onChange={(pageNumber: number) => {
                    onPageChange(pageNumber)
                  }}
                  disabled={disabled}
                  pageNumberButtonType='outline'
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <Spinner />
      )}
    </>
  )
}

export default Table
